import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { IMetadataRegistryEntry } from "../../Orchestrator/Types/Interface/IMetadataRegistry";
import { loadRegistryEntries, parseRegistryMinutes, peekRegistryEntries } from "./metadataRegistryCache";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * How long a ticket keeps showing under Upcoming after its train has departed.
 *
 * Without it a booking flips to Past the second the train pulls out, so a
 * passenger sitting on a moving train has to dig through Past bookings to show
 * their own ticket. The period is the grace window that keeps the ticket where
 * they will look for it.
 *
 * Measured from DEPARTURE, not arrival: `travelDateRange` on
 * POST /bookings-search/custom filters on the trip start time, so departure is
 * the only pivot the server-side half of this can express — and a pivot the
 * client applied differently from the query would put a booking in both tabs
 * or in neither.
 */
export const BOOKING_APPEARANCE_REGISTRY_KEY = "booking_ticket_appearance_period";

/**
 * Used when the registry has no row for the key — it is seeded on preprod (60)
 * but not on every environment. The fallback is a real value rather than 0
 * because 0 restores exactly the bug this exists to fix, and an unseeded
 * environment should behave like a seeded one, not like the old build.
 */
export const DEFAULT_BOOKING_APPEARANCE_MINUTES = 60;

/**
 * A day, agreed with the mobile app so one registry value means one thing on
 * both platforms. This key does NOT take the parser's default 240-minute
 * ceiling: that one is sized for the `transfert_*` transfer gaps, where four
 * hours is already absurd, and widening it there would loosen a guard that is
 * load-bearing for shuttles. Past a day this stops being a policy and starts
 * dragging genuinely finished journeys back into Upcoming, so it is still a
 * typo guard — just a looser one.
 */
const MAX_APPEARANCE_MINUTES = 24 * 60;

/**
 * A value above MAX_APPEARANCE_MINUTES is rejected back to the fallback with a
 * console warning, not clamped — silently honouring half of a wrong value hides
 * the mistake.
 */
export function parseBookingAppearanceMinutes(
    entries: IMetadataRegistryEntry[] | null | undefined,
    fallback: number = DEFAULT_BOOKING_APPEARANCE_MINUTES
): number {
    // allowZero: 0 means "move to Past at departure" — the behaviour before this
    // key existed, and a setting ops may legitimately choose, not an unfilled row.
    return parseRegistryMinutes(entries, BOOKING_APPEARANCE_REGISTRY_KEY, fallback, {
        allowZero: true,
        max: MAX_APPEARANCE_MINUTES,
    });
}

/**
 * The period from the warm registry cache.
 *
 * Every pivot below reads it synchronously: the decision sites are scattered
 * across a render pass and one 3000-line action, and threading an awaited value
 * through all of them would be a far larger change than the fix.
 *
 * That makes warming the cache a contract on the entry points, so here they all
 * are — anything new that reaches a pivot must await `loadBookingAppearanceMinutes`
 * the same way:
 *   - UpcomingPastBookingTabAction        (both tabs' searches)
 *   - UpcomingPastBookingMonthFilterAction (Past month filter)
 *   - GetBookingIsPastOrUpcoming          (guest PNR lookup)
 *   - GetBookingDetailOfRetrieveBookingAction (reload onto a cached guest PNR)
 *   - GetBookingDetailDataAction           (booking details, incl. deep links)
 *   - GetBookingDetailStatusAction         (the PAST stamp on booking details)
 * LoadMoreBookingsAction needs nothing: it can only run once one of the two
 * searches above has populated the pagination atom.
 *
 * A cold read falls back rather than throwing — but note what a cold read costs:
 * the two halves of a tab decision would then disagree, so this is a contract to
 * keep, not a safety net to lean on.
 */
export function bookingAppearanceMinutes(): number {
    return parseBookingAppearanceMinutes(peekRegistryEntries());
}

export async function loadBookingAppearanceMinutes(): Promise<number> {
    return parseBookingAppearanceMinutes(await loadRegistryEntries());
}

/**
 * The instant a trip must have departed before to count as past: now minus the
 * appearance period.
 *
 * Every upcoming/past decision pivots on this one value — the two search
 * ranges, the list filter, the round-trip split, the guest PNR lookup — so the
 * two tabs stay complementary. One site left on a bare `now` is enough to make
 * a booking appear twice or vanish.
 */
export function bookingsPastPivot(now: Date = new Date()): Date {
    return new Date(now.getTime() - bookingAppearanceMinutes() * 60_000);
}

/**
 * Which tab a departure belongs to — THE definition, so the two halves cannot
 * drift apart.
 *
 * `>` on one side and `<=` on the other around one pivot is what makes the tabs
 * complementary: every instant satisfies exactly one, including the pivot
 * itself. Written once and exported so the test can bind to this rather than to
 * a copy of it; a copy goes green no matter what the list filter does.
 */
export function isDepartureInTab(
    departureIso: string,
    activeTab: string,
    pivotIso: string
): boolean {
    return activeTab === "1" ? departureIso > pivotIso : departureIso <= pivotIso;
}

/** The same pivot in Asia/Dubai, for the split rules that compare in local time. */
export function bookingsPastPivotDubai() {
    return dayjs().tz("Asia/Dubai").subtract(bookingAppearanceMinutes(), "minute");
}
